import { SUPPORTER_ID } from '@avanta/shared/constants/chat';
import { useConversationsStore } from '@avanta/shared/stores/chat/conversations';
import { useChatStore } from '@avanta/shared/stores/chat/index';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { ROUTES } from 'constants/routes';
import { useUserStore } from 'stores/user';

export const useChatInitializer = (): void => {
  const user = useUserStore(state => state.user);
  const initChat = useChatStore(state => state.initChat);
  const isInitialized = useChatStore(state => state.isInitialized);
  const loadConversations = useConversationsStore(state => state.loadConversations);
  const createConversation = useConversationsStore(state => state.createConversation);
  const { state } = useLocation();
  const lastCreatedTargetId = useRef<string | null>(null);
  const pendingTargetId = useRef<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userName =
    user?.firstName && user?.surname
      ? `${user.firstName} ${user.surname}`
      : user?.name || user?.nickname;

  useEffect(() => {
    if (!user) return;
    initChat(user.id, userName ?? undefined);
  }, [user, initChat, userName]);

  useEffect(() => {
    if (!isInitialized) return;
    loadConversations();
  }, [isInitialized, loadConversations]);

  useEffect(() => {
    if (!isInitialized) return;

    const targetId = state?.support || searchParams.get('support') ? SUPPORTER_ID : null;

    if (
      !targetId ||
      lastCreatedTargetId.current === targetId ||
      pendingTargetId.current === targetId
    ) {
      return;
    }

    pendingTargetId.current = targetId;
    void createConversation(targetId, () => navigate(ROUTES.HOME))
      .then(conversation => {
        if (conversation) {
          lastCreatedTargetId.current = targetId;
        }
      })
      .finally(() => {
        if (pendingTargetId.current === targetId) {
          pendingTargetId.current = null;
        }
      });
  }, [isInitialized, state, createConversation, navigate, searchParams]);
};
